import type {
  MultiViewFilterClause,
  MultiViewSharedQueryState,
} from '../../../core/multi-view';
import type { FolderRecord } from '../../data-binding/models/FolderRecord';
import {
  OrgAllocationService,
  type OrgAllocationAssignment,
} from './OrgAllocationService';

type Comparable = string | number | boolean | null;

const SOURCE_FIELD_ALIASES: Record<string, readonly string[]> = {
  manager: ['Manager', 'Manager Name', 'Manager Email', 'Reports To', 'Supervisor'],
  team: ['Team', 'Squad', 'Group', 'Pod'],
  department: ['Department', 'Dept', 'Function', 'Org Unit', 'Business Unit'],
};

export class OrgAllocationSharedQueryService {
  constructor(private readonly allocationService = new OrgAllocationService()) {}

  filterRelationshipRecords(
    relationshipRecords: readonly FolderRecord[],
    sharedQuery?: MultiViewSharedQueryState,
  ): FolderRecord[] {
    if (!hasActiveQuery(sharedQuery)) return [...relationshipRecords];

    const assignmentByRecordId = new Map<string, OrgAllocationAssignment>();
    for (const record of relationshipRecords) {
      const assignment = this.allocationService.relationshipRecordToAssignment(record);
      if (assignment) assignmentByRecordId.set(record.id, assignment);
    }

    const loadByPerson = totalAllocationByPerson(Array.from(assignmentByRecordId.values()));
    return relationshipRecords.filter((record) => {
      const assignment = assignmentByRecordId.get(record.id);
      if (!assignment) return false;
      if (!matchesSearch(assignment, sharedQuery?.search)) return false;
      if (!matchesTarget(assignment, sharedQuery?.target)) return false;
      if (!matchesTime(assignment, sharedQuery?.time)) return false;
      if (!matchesFacets(assignment, sharedQuery?.facets, loadByPerson)) return false;
      return (sharedQuery?.filters ?? []).every((filter) =>
        matchesFilter(assignment, filter, loadByPerson),
      );
    });
  }
}

function hasActiveQuery(sharedQuery?: MultiViewSharedQueryState): boolean {
  if (!sharedQuery) return false;
  if (sharedQuery.search?.trim()) return true;
  if ((sharedQuery.filters ?? []).length > 0) return true;
  if (Object.keys(sharedQuery.facets ?? {}).length > 0) return true;
  if (sharedQuery.target?.id) return true;
  if (sharedQuery.time && Object.values(sharedQuery.time).some((value) => value != null && value !== '')) return true;
  return false;
}

function matchesSearch(assignment: OrgAllocationAssignment, search?: string): boolean {
  const needle = search?.trim().toLowerCase();
  if (!needle) return true;
  return [
    assignment.actorExternalId,
    assignment.actorLabel,
    assignment.personExternalId,
    assignment.personLabel,
    assignment.targetExternalId,
    assignment.targetLabel,
    assignment.targetKind,
    assignment.role,
    assignment.relationshipRecordId,
    assignment.relationshipExternalId,
    assignment.lifecycleState,
    assignment.recordStatus,
    assignment.sourceStatus,
    assignment.sourceBindingStatus,
    stringValue(readPath(assignment.source, 'label')),
    stringValue(readPath(assignment.source, 'connectorId')),
    stringValue(readPath(assignment.source, 'system')),
    stringValue(readPath(assignment.source, 'sourceSystem')),
    ...sourceRowValues(assignment.source),
  ].some((value) => value?.toLowerCase().includes(needle));
}

function matchesTarget(
  assignment: OrgAllocationAssignment,
  target: MultiViewSharedQueryState['target'] | undefined,
): boolean {
  if (!target?.id) return true;
  if (target.kind === 'person') return assignment.personExternalId === target.id;
  return assignment.targetExternalId === target.id;
}

function matchesTime(
  assignment: OrgAllocationAssignment,
  time: MultiViewSharedQueryState['time'] | undefined,
): boolean {
  if (!time) return true;
  const windowStart = toDate(time.windowStart);
  const windowEnd = toDate(time.windowEnd);
  if (windowStart || windowEnd) {
    return overlapsWindow(assignment, windowStart, windowEnd);
  }
  const asOf = toDate(time.asOf);
  return asOf ? isActiveAt(assignment, asOf) : true;
}

function matchesFacets(
  assignment: OrgAllocationAssignment,
  facets: Readonly<Record<string, readonly string[]>> | undefined,
  loadByPerson: ReadonlyMap<string, number>,
): boolean {
  if (!facets || Object.keys(facets).length === 0) return true;
  return Object.entries(facets).every(([field, values]) => {
    if (values.length === 0) return true;
    const value = valueForFilter(assignment, {
      id: `facet:${field}`,
      field,
      operator: 'in',
      values,
    }, loadByPerson);
    return values.some((candidate) => compareValues(value, candidate) === 0);
  });
}

function matchesFilter(
  assignment: OrgAllocationAssignment,
  filter: MultiViewFilterClause,
  loadByPerson: ReadonlyMap<string, number>,
): boolean {
  const value = valueForFilter(assignment, filter, loadByPerson);
  switch (filter.operator) {
    case 'eq':
      return compareValues(value, filter.value) === 0;
    case 'neq':
      return compareValues(value, filter.value) !== 0;
    case 'contains':
      return stringValue(value)?.toLowerCase().includes(String(filter.value ?? '').toLowerCase()) ?? false;
    case 'in':
      return (filter.values ?? []).some((candidate) => compareValues(value, candidate) === 0);
    case 'not_in':
      return !(filter.values ?? []).some((candidate) => compareValues(value, candidate) === 0);
    case 'gte':
      return numberValue(value) >= numberValue(filter.value);
    case 'lte':
      return numberValue(value) <= numberValue(filter.value);
    case 'between': {
      const [min, max] = filter.values ?? [];
      const numeric = numberValue(value);
      return numeric >= numberValue(min) && numeric <= numberValue(max);
    }
    case 'exists':
      return value !== null && value !== undefined && value !== '';
    default:
      return true;
  }
}

function valueForFilter(
  assignment: OrgAllocationAssignment,
  filter: MultiViewFilterClause,
  loadByPerson: ReadonlyMap<string, number>,
): unknown {
  if (filter.field === 'totalAllocation') {
    return loadByPerson.get(assignment.personExternalId) ?? 0;
  }
  if (filter.field === 'availableAllocation' || filter.field === 'available') {
    return Math.max(0, 1 - (loadByPerson.get(assignment.personExternalId) ?? 0));
  }
  if (filter.field === 'sourceState' || filter.field === 'resolutionStatus') {
    return sourceStateForAssignment(assignment);
  }
  if (filter.field === 'unresolved') {
    return sourceStateForAssignment(assignment) === 'unresolved';
  }
  if (filter.field === 'sourceLabel') {
    return sourceLabelForAssignment(assignment);
  }
  if (filter.field === 'sourceSystem') {
    return stringValue(readPath(assignment.source, 'sourceSystem')) ??
      stringValue(readPath(assignment.source, 'connectorId')) ??
      stringValue(readPath(assignment.source, 'system'));
  }
  if (filter.field === 'confidence') {
    return assignment.confidence;
  }
  const direct = readPath(assignment, filter.field);
  if (direct !== undefined) return direct;
  return sourceRowValue(assignment.source, filter.field);
}

function totalAllocationByPerson(
  assignments: readonly OrgAllocationAssignment[],
): ReadonlyMap<string, number> {
  const totals = new Map<string, number>();
  for (const assignment of assignments) {
    totals.set(
      assignment.personExternalId,
      (totals.get(assignment.personExternalId) ?? 0) + (assignment.allocation ?? 0),
    );
  }
  return totals;
}

function readPath(input: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((current, segment) => {
    if (!isRecord(current)) return undefined;
    return current[segment];
  }, input);
}

function isActiveAt(assignment: OrgAllocationAssignment, asOf: Date): boolean {
  const start = toDate(assignment.validFrom);
  const end = toDate(assignment.validTo);
  if (start && start.getTime() > asOf.getTime()) return false;
  if (end && end.getTime() < asOf.getTime()) return false;
  return true;
}

function overlapsWindow(
  assignment: OrgAllocationAssignment,
  windowStart: Date | null,
  windowEnd: Date | null,
): boolean {
  const start = toDate(assignment.validFrom);
  const end = toDate(assignment.validTo);
  const effectiveStart = start?.getTime() ?? Number.NEGATIVE_INFINITY;
  const effectiveEnd = end?.getTime() ?? Number.POSITIVE_INFINITY;
  const queryStart = windowStart?.getTime() ?? Number.NEGATIVE_INFINITY;
  const queryEnd = windowEnd?.getTime() ?? Number.POSITIVE_INFINITY;
  return effectiveStart <= queryEnd && effectiveEnd >= queryStart;
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function compareValues(left: unknown, right: unknown): number {
  const leftComparable = comparableValue(left);
  const rightComparable = comparableValue(right);
  if (leftComparable === rightComparable) return 0;
  if (typeof leftComparable === 'number' && typeof rightComparable === 'number') {
    return leftComparable < rightComparable ? -1 : 1;
  }
  return String(leftComparable).localeCompare(String(rightComparable));
}

function comparableValue(value: unknown): Comparable {
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim().toLowerCase();
  return value == null ? null : String(value).trim().toLowerCase();
}

function numberValue(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function sourceStateForAssignment(assignment: OrgAllocationAssignment): string {
  if (assignment.basis?.toLowerCase() === 'scenario' || assignment.source.scenarioOverlay === true) return 'draft';
  if (hasState(assignment, ['retired', 'tombstoned', 'deleted', 'inactive'])) return 'retired';
  if (hasState(assignment, ['unresolved', 'resolution_issue', 'mapping_issue'])) return 'unresolved';
  if (hasState(assignment, ['needs_review', 'awaiting_review', 'review'])) return 'needs_review';
  if (hasState(assignment, ['draft', 'proposed', 'scenario'])) return 'draft';
  if (hasState(assignment, ['stale', 'expired'])) return 'stale';
  if (typeof assignment.confidence === 'number' && Number.isFinite(assignment.confidence) && assignment.confidence < 0.7) {
    return 'low_confidence';
  }
  return 'source_backed';
}

function hasState(
  assignment: OrgAllocationAssignment,
  states: readonly string[],
): boolean {
  const stateValues = [
    assignment.lifecycleState,
    assignment.recordStatus,
    assignment.sourceStatus,
    assignment.sourceBindingStatus,
    stringValue(assignment.source.status),
    stringValue(assignment.source.state),
    stringValue(assignment.source.lifecycleState),
  ].map((value) => value?.toLowerCase().replace(/[\s-]+/g, '_'));
  return stateValues.some((value) => !!value && states.includes(value));
}

function sourceLabelForAssignment(assignment: OrgAllocationAssignment): string | null {
  return stringValue(assignment.source.label) ??
    stringValue(assignment.source.name) ??
    stringValue(assignment.source.fileName) ??
    stringValue(assignment.source.sourceSystem);
}

function sourceRowValue(source: unknown, field: string): unknown {
  if (!isRecord(source) || !isRecord(source.rowSnapshot)) return undefined;
  const fieldAliases = SOURCE_FIELD_ALIASES[normalizeFieldName(field)] ?? [field];
  const normalizedFields = new Set(fieldAliases.map(normalizeFieldName));
  for (const [key, value] of Object.entries(source.rowSnapshot)) {
    if (normalizedFields.has(normalizeFieldName(key))) return value;
  }
  return undefined;
}

function sourceRowValues(source: unknown): string[] {
  if (!isRecord(source) || !isRecord(source.rowSnapshot)) return [];
  return Object.values(source.rowSnapshot)
    .map(stringValue)
    .filter((value): value is string => !!value);
}

function normalizeFieldName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}
